import React, { useEffect, useState, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import mn from "date-fns/locale/mn";
import { getAuth, signOut } from "firebase/auth";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
    FiClock, FiLogIn, FiLogOut, FiUnlock, 
    FiSearch, FiMenu, FiHome, FiLock,
    FiCalendar, FiActivity
} from "react-icons/fi";

import { 
    doc, onSnapshot, updateDoc, getFirestore, 
    orderBy, where, collection, query, 
    addDoc, getDoc, getDocs, limit
} from "firebase/firestore";

registerLocale("mn", mn);
const MySwal = withReactContent(Swal);

export default function Garage() {
    const [parkingLogs, setParkingLogs] = useState([]);
    const [systemStatus, setSystemStatus] = useState({ camera: true, gate: true }); 
    const [searchPlate, setSearchPlate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDate, setSelectedDate] = useState(new Date()); 
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [userRole, setUserRole] = useState("operator");
    const [isLoadingData, setIsLoadingData] = useState(true);
    
    const db = getFirestore(); 
    const auth = getAuth();
    const pageSize = 100;

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            window.location.href = "/login"; 
        } catch (error) {
            MySwal.fire({ icon: "error", title: "Гарах үед алдаа гарлаа", text: error.message });
        }
    };
    
    const formatTime = (timestamp) => {
        if (!timestamp?.toDate) return "-";
        return timestamp.toDate().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' });
    };

    const fetchUserRole = async () => {
        if (!auth.currentUser || !auth.currentUser.email) return;
        try {
            const userDocRef = doc(db, "parkings", auth.currentUser.displayName, "users", auth.currentUser.email); 
            const userSnap = await getDoc(userDocRef);
            if(userSnap.exists()){
                const data = userSnap.data();
                setUserRole(data['role']==="master" ? "master" : data['role'] === "admin" ? "admin" : "operator");
            }
        } catch (err) {
            console.error("Эрх шалгахад алдаа гарлаа:", err);
        }
    };

    useEffect(() => {
        let unsubscribeLogs = null;
        let unsubscribeStatus = null;

        const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            fetchUserRole();
            setIsLoadingData(true);

            const recordsColRef = collection(db, "parkings", user.displayName, "garageRecords");
            const startOfDay = new Date(selectedDate);
            startOfDay.setHours(0, 0, 0, 0); 
            const endOfDay = new Date(selectedDate);
            endOfDay.setHours(23, 59, 59, 999); 

            // Хүснэгтэд харуулах дата (Limit 500)
            const today = new Date();
            const isToday = selectedDate.getDate() === today.getDate() && 
                            selectedDate.getMonth() === today.getMonth() && 
                            selectedDate.getFullYear() === today.getFullYear();

            const qLogs = query(
                recordsColRef,
                where("entry_time", ">=", startOfDay), 
                where("entry_time", "<=", endOfDay), 
                orderBy("entry_time", "desc"),
                limit(500)
            );

            if (isToday) {
                // Өнөөдөр бол Realtime
                unsubscribeLogs = onSnapshot(qLogs, (querySnapshot) => {
                    const logsArray = querySnapshot.docs.map(docSnapshot => ({
                        id: docSnapshot.id,
                        ...docSnapshot.data()
                    }));
                    setParkingLogs(logsArray);
                    setIsLoadingData(false);
                }, (error) => {
                    console.error("Лог татахад алдаа гарлаа:", error);
                    setIsLoadingData(false);
                });
            } else {
                // Өнгөрсөн өдөр бол 1 л удаа татна
                try {
                    const querySnapshot = await getDocs(qLogs);
                    const logsArray = querySnapshot.docs.map(docSnapshot => ({
                        id: docSnapshot.id,
                        ...docSnapshot.data()
                    }));
                    setParkingLogs(logsArray);
                } catch (error) {
                    console.error("Түүхэн лог татахад алдаа гарлаа: ", error);
                } finally {
                    setIsLoadingData(false);
                }
            }

            // Төхөөрөмжийн статус шалгах
            const statusDocRef = doc(db, "parkings", user.displayName);
            unsubscribeStatus = onSnapshot(statusDocRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data();
                    setSystemStatus(prev => ({
                        ...prev,
                        camera: data.sensor_status?.camera ?? true,
                        gate: data.sensor_status?.gate ?? true
                    }));
                }
            });
        });

        return () => {
            if (unsubscribeLogs) unsubscribeLogs();
            if (unsubscribeStatus) unsubscribeStatus();
            unsubscribeAuth();
        };
    }, [db, selectedDate]); 

    const filteredLogs = useMemo(() => {
        let result = parkingLogs;
        if (searchPlate) {
            result = result.filter(log => String(log.car_plate || log.plate || "").toLowerCase().includes(searchPlate.toLowerCase()));
        }
        return result;
    }, [parkingLogs, searchPlate]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
    const paginatedLogs = filteredLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const openGate = async (gateName) => {
        try {
            const gateControlRef = doc(db, "parkings", auth.currentUser.displayName); 
            await updateDoc(gateControlRef, { [gateName]: true });
            MySwal.fire({ title: "Хаалга нээгдлээ", icon: "success", showConfirmButton: false, timer: 1500 });
        } catch (e) {
            MySwal.fire({ title: "Алдаа гарлаа", icon: "error" });
        }
    };
    
    const handleDateChange = (date) => {
        setSelectedDate(date);
        setCurrentPage(1); 
    };

    const addBlock = async (plate) => {
        try {
            const colRef = collection(db, "parkings", auth.currentUser.displayName, "registeredCars");
            const q = query(colRef, where("plate", "==", plate));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                return MySwal.fire({ icon: "info", title: "Энэ машиныг бүртгэлтэй хэсгээс блоклоно уу" });
            } 
            
            const result = await MySwal.fire({
                title: "Блоклох уу?",
                text: `${plate} дугаартай машиныг блоклохдоо итгэлтэй байна уу?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#ef4444",
                cancelButtonColor: "#3b82f6",
                confirmButtonText: "Тийм",
                cancelButtonText: "Цуцлах"
            });

            if (result.isConfirmed) {
                await addDoc(colRef, {
                    building: "Гандны",
                    number: "Бүртгэлгүй",
                    isBlocked: true,
                    plate: plate,
                    phone: "-",
                    desc: "Гаражийн зөрчил",
                    createdAt: new Date()
                });
                MySwal.fire({ icon: "success", title: "Блоклогдлоо", showConfirmButton: false, timer: 1500 });
            }
        } catch (error) {
            MySwal.fire({ icon: "error", title: "Алдаа гарлаа", text: "Хадгалж чадсангүй." });
        }
    };

    return (
        <div className="tw-bg-slate-50 tw-min-h-screen tw-p-3 sm:tw-p-6 lg:tw-p-8 tw-font-sans tw-text-slate-800 tw-max-w-[100vw] tw-overflow-x-hidden">
            
            {/* 1. Толгойн хэсэг */}
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-mb-6 sm:tw-mb-8 tw-gap-4">
                <div className="tw-flex tw-items-center tw-gap-4">
                    <div>
                        <h2 className="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-m-0">Гаражийн Удирдлага</h2>
                        <p className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-mt-1 tw-mb-0">Дотор зогсоолын нэгдсэн хяналт</p>
                    </div>
                </div>

                <div className="tw-flex tw-w-full md:tw-w-auto tw-items-center tw-justify-between md:tw-justify-end tw-gap-2 sm:tw-gap-3">
                    <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-shadow-sm tw-px-3 tw-py-2 tw-w-full sm:tw-w-auto">
                        <FiCalendar className="tw-text-slate-400 tw-mr-2" size={18} />
                        <DatePicker
                            selected={selectedDate}
                            onChange={handleDateChange}
                            dateFormat="yyyy-MM-dd"
                            maxDate={new Date()}
                            locale="mn"
                            popperPlacement="bottom-end"
                            className="tw-bg-transparent tw-text-sm tw-font-bold tw-text-slate-700 tw-outline-none tw-w-full sm:tw-w-28 tw-cursor-pointer"
                        />
                    </div>
                    
                    <button 
                        onClick={handleLogout} 
                        className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-slate-200 tw-text-slate-600 tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-sm hover:tw-bg-slate-50 tw-flex-shrink-0 tw-transition-colors"
                    >
                        <FiLogOut size={16} /> <span className="tw-hidden sm:tw-inline">Гарах</span>
                    </button>
                </div>
            </div>

            {/* 2. Гаражийн хаалганы удирдлага */}
            <div className="tw-bg-white tw-p-4 sm:tw-p-6 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-mb-6 sm:tw-mb-8">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                    <FiActivity className="tw-text-indigo-600" size={20} />
                    <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-slate-800 tw-m-0">Ухаалаг Хаалга Удирдах</h3>
                </div>
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 sm:tw-gap-4">
                    <button 
                        onClick={() => openGate("garageGate.enterOpen")}
                        className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-200 tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-3.5 tw-rounded-xl tw-font-bold hover:tw-bg-emerald-100 active:tw-scale-95 tw-transition-transform"
                    >
                        <FiLogIn size={18} /> Орох хаалга нээх
                    </button>
                    <button 
                        onClick={() => openGate("garageGate.exitOpen")}
                        className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-indigo-50 tw-text-indigo-700 tw-border tw-border-indigo-200 tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-3.5 tw-rounded-xl tw-font-bold hover:tw-bg-indigo-100 active:tw-scale-95 tw-transition-transform"
                    >
                        <FiLogOut size={18} /> Гарах хаалга нээх
                    </button>
                </div>
            </div>

            {/* 3. Хайлт */}
            <div className="tw-bg-white tw-rounded-t-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-border-b-0 tw-p-4">
                <div className="tw-relative tw-w-full md:tw-w-96">
                    <FiSearch className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={18} />
                    <input 
                        type="text"
                        placeholder="Улсын дугаараар хайх..."
                        value={searchPlate}
                        onChange={(e) => {setSearchPlate(e.target.value); setCurrentPage(1);}}
                        className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500 focus:tw-ring-1 focus:tw-ring-indigo-500 tw-transition-all"
                    />
                </div>
            </div>

            {/* 4. Хүснэгт */}
            <div className="tw-bg-white tw-shadow-sm tw-border tw-border-slate-200 tw-rounded-b-2xl tw-w-full tw-overflow-hidden">
                <div className="tw-overflow-x-auto tw-w-full tw-block">
                    <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[600px] md:tw-min-w-[800px]">
                        <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
                            <tr>
                                <th className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-w-12 sm:tw-w-16">№</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Дугаар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Орсон Цаг</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Гарсан Цаг</th>
                                <th className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-right">Үйлдэл</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-slate-100">
                            {isLoadingData ? (
                                <tr>
                                    <td colSpan={5} className="tw-px-6 tw-py-16 sm:tw-py-20 tw-text-center">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-indigo-500">
                                            <div className="tw-w-8 sm:tw-w-10 tw-h-8 sm:tw-h-10 tw-border-4 tw-border-indigo-100 tw-border-t-indigo-500 tw-rounded-full tw-animate-spin tw-mb-4"></div>
                                            <span className="tw-text-sm tw-font-bold tw-text-slate-600">Мэдээлэл уншиж байна...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="tw-px-6 tw-py-16 sm:tw-py-20 tw-text-center">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-slate-400">
                                            <FiSearch size={40} className="tw-mb-4 tw-text-slate-300" />
                                            <span className="tw-text-sm tw-font-medium">Мэдээлэл олдсонгүй.</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedLogs.map((log, idx) => (
                                <tr key={log.id} className="hover:tw-bg-slate-50 tw-transition-colors">
                                    <td className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-400">
                                        {(currentPage - 1) * pageSize + idx + 1}
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4">
                                        <div className="tw-inline-block tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-800 tw-tracking-wider">
                                            {String(log.car_plate || log.plate || "-")}
                                        </div>
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 sm:tw-gap-2 whitespace-nowrap">
                                            <div className="tw-w-5 sm:tw-w-6 tw-h-5 sm:tw-h-6 tw-rounded-full tw-bg-emerald-50 tw-text-emerald-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <FiClock size={12} />
                                            </div>
                                            {formatTime(log.entry_time)}
                                        </div>
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 sm:tw-gap-2 whitespace-nowrap">
                                            <div className="tw-w-5 sm:tw-w-6 tw-h-5 sm:tw-h-6 tw-rounded-full tw-bg-rose-50 tw-text-rose-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <FiClock size={12} />
                                            </div>
                                            {formatTime(log.exit_time)}
                                        </div>
                                    </td>
                                    <td className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-right">
                                        <button 
                                            onClick={() => addBlock(log.car_plate || log.plate)} 
                                            className={`tw-flex tw-items-center tw-justify-center tw-w-7 sm:tw-w-9 tw-h-7 sm:tw-h-9 tw-rounded-lg tw-border tw-transition-colors tw-ml-auto ${
                                                log.registered 
                                                ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-200 hover:tw-bg-emerald-100' 
                                                : 'tw-bg-slate-50 tw-text-slate-400 tw-border-slate-200 hover:tw-bg-rose-50 hover:tw-text-rose-600 hover:tw-border-rose-200'
                                            }`}
                                            title={log.registered ? "Бүртгэлтэй" : "Блоклох"}
                                        >
                                            {log.registered ? <FiHome size={14} className="sm:tw-w-[18px] sm:tw-h-[18px]" /> : <FiLock size={14} className="sm:tw-w-[18px] sm:tw-h-[18px]" />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {filteredLogs.length > 0 && (
                    <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3">
                        <div className="tw-text-xs sm:tw-text-sm tw-text-slate-500">
                            Жагсаалтад: <span className="tw-font-bold tw-text-slate-900">{filteredLogs.length}</span> бичлэг (Сүүлийн 500-аас)
                        </div>
                        <div className="tw-flex tw-items-center tw-w-full sm:tw-w-auto tw-gap-2 tw-justify-between sm:tw-justify-end">
                            <button 
                                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} 
                                disabled={currentPage === 1}
                                className={`tw-flex-1 sm:tw-flex-none tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-colors ${currentPage === 1 ? 'tw-bg-slate-100 tw-text-slate-400 tw-cursor-not-allowed tw-border tw-border-transparent' : 'tw-bg-white tw-border tw-border-slate-200 tw-text-slate-700 hover:tw-bg-slate-50'}`}
                            >
                                Өмнөх
                            </button>
                            <div className="tw-flex tw-items-center tw-justify-center tw-px-3 sm:tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700">
                                {currentPage} / {totalPages}
                            </div>
                            <button 
                                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} 
                                disabled={currentPage === totalPages}
                                className={`tw-flex-1 sm:tw-flex-none tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-colors ${currentPage === totalPages ? 'tw-bg-slate-100 tw-text-slate-400 tw-cursor-not-allowed tw-border tw-border-transparent' : 'tw-bg-white tw-border tw-border-slate-200 tw-text-slate-700 hover:tw-bg-slate-50'}`}
                            >
                                Дараах
                            </button>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}